'use client';
import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercentage = Math.min(window.scrollY / (window.innerHeight * 1.5), 1);
      setScrollY(window.scrollY);
      setIsExpanded(scrollPercentage > 0.3);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen pt-0">
      <div className="mt-0 sm:mt-0 md:mt-0 min-h-screen w-full overflow-hidden flex items-center justify-center">
        {/* Animated Background Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Gradient Orbs */}
          <div
            className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-amber-500/20 to-purple-500/20 rounded-full blur-3xl animate-float"
            style={{ transform: `translateY(${scrollY * 0.1}px)` }} />
          
          <div
            className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-float-delayed"
            style={{ transform: `translateY(${scrollY * -0.15}px)` }} />
          
          <div
            className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-full blur-2xl animate-pulse-slow"
            style={{ transform: `translateY(${scrollY * 0.2}px)` }} />
          
          
          {/* Floating Cards */}
          <div
            className="absolute top-32 right-20 w-32 h-32 bg-zinc-900/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl rotate-12 animate-float"
            style={{ transform: `translateY(${scrollY * 0.08}px) rotate(12deg)` }} />
          
          <div
            className="absolute bottom-40 left-32 w-40 h-40 bg-zinc-900/40 backdrop-blur-sm border border-purple-500/20 rounded-2xl -rotate-6 animate-float-delayed"
            style={{ transform: `translateY(${scrollY * -0.12}px) rotate(-6deg)` }} />
          
          
          {/* Geometric Shapes */}
          <div
            className="absolute top-1/4 right-1/3 w-24 h-24 border-2 border-amber-500/30 rotate-45 animate-spin-slow"
            style={{ transform: `translateY(${scrollY * 0.15}px) rotate(${45 + scrollY * 0.1}deg)` }} />
          
          <div
            className="absolute bottom-1/3 left-1/4 w-16 h-16 border-2 border-purple-500/30 rounded-full animate-pulse-slow"
            style={{ transform: `translateY(${scrollY * -0.1}px) scale(${1 + Math.sin(scrollY * 0.01) * 0.2})` }} />
          
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#27272a_1px,transparent_1px)] bg-[size:4rem_4rem]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,#27272a_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6">
          {/* Compact Catchy Introduction Header - Optimized for Mobile */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8 animate-fade-in">
            <div className="inline-block mb-2 sm:mb-3 px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 border border-amber-500/30 rounded-full">
              <span className="text-[9px] sm:text-[10px] md:text-sm font-bold text-amber-400 uppercase tracking-wider">Classic24 Digital Solutions</span>
            </div>
            <h2 className="text-sm sm:text-base md:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 leading-tight animate-slide-up">
              Your Growth Partner in the
              <span className="block gradient-text mt-1">Digital Revolution</span>
            </h2>
          </div>

          {/* Main Headline - Optimized for Mobile Visibility */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4 md:mb-6 animate-fade-in">
              <div className="w-[2px] h-3 sm:h-4 bg-amber-500" />
              <span className="text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold text-zinc-400">
                Digital Excellence
              </span>
            </div>
            <h1 className="pt-32 sm:pt-0 text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tight mb-4 sm:mb-6 md:mb-8 leading-tight sm:leading-[0.9] break-words animate-slide-up">
              <span className="block text-white break-words">Transform Your</span>
              <span className="block gradient-text break-words">Digital Presence</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-lg lg:text-xl text-zinc-400 max-w-2xl mx-auto mb-8 sm:mb-10 md:mb-12 px-4 animate-fade-in-delay leading-relaxed">
              Leading digital marketing agency in Ulhasnagar specializing in social media marketing, web development, and creative design for 50+ industries.
            </p>
          </div>

          {/* Creative Video Intro Section */}
          <div className="mb-12 sm:mb-16 md:mb-20 reveal">
            <div className="relative group">
              {/* Animated Gradient Border Container */}
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-3xl blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient-x"></div>
              
              {/* Video Container */}
              <div className="relative bg-zinc-900/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl overflow-hidden border border-purple-500/30">
                {/* Decorative Corner Elements */}
                <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-pink-500 rounded-tl-2xl opacity-50"></div>
                <div className="absolute top-0 right-0 w-20 h-20 border-t-4 border-r-4 border-purple-500 rounded-tr-2xl opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 border-b-4 border-l-4 border-purple-500 rounded-bl-2xl opacity-50"></div>
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-pink-500 rounded-br-2xl opacity-50"></div>
                
                {/* Video Header */}
                <div className="relative z-10 text-center pt-6 sm:pt-8 pb-4 sm:pb-6 px-4">
                  <div className="inline-flex items-center gap-2 mb-3 px-4 py-2 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-full">
                    <span className="text-2xl animate-pulse">🎬</span>
                    <span className="text-xs sm:text-sm font-bold text-pink-400 uppercase tracking-wider">Watch Our Story</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                    Discover How We <span className="gradient-text">Transform Brands</span>
                  </h3>
                  <p className="text-xs sm:text-sm text-zinc-400 max-w-2xl mx-auto">
                    See how Classic24 brings digital dreams to life through innovation and creativity
                  </p>
                </div>

                {/* YouTube Video Embed */}
                <div className="relative aspect-video w-full px-4 sm:px-6 md:px-8 pb-6 sm:pb-8">
                  <div className="relative w-full h-full rounded-xl overflow-hidden shadow-2xl group-hover:shadow-pink-500/20 transition-shadow duration-300">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/VhEFwEZ9KLM?rel=0&modestbranding=1"
                      title="Classic24 Introduction Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                {/* Floating Particles Effect */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-pink-500 rounded-full animate-float opacity-60"></div>
                  <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-purple-500 rounded-full animate-float-delayed opacity-40"></div>
                  <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-pink-400 rounded-full animate-pulse-slow opacity-50"></div>
                  <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-purple-400 rounded-full animate-float opacity-30"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-12 md:mb-16">
            {[
            { value: '500+', label: 'Projects Delivered', icon: '🚀' },
            { value: '50+', label: 'Industries Served', icon: '🏢' },
            { value: '10+', label: 'Years Experience', icon: '⭐' },
            { value: '95%', label: 'Client Satisfaction', icon: '💯' }]?.map((stat, idx) =>
            <div
              key={idx}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 text-center hover:border-amber-500/30 transition-all hover:scale-105 glow-hover reveal"
              style={{ transitionDelay: `${idx * 100}ms` }}>
              
                <div className="text-xl sm:text-2xl md:text-3xl mb-1 sm:mb-2">{stat?.icon}</div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-0.5 sm:mb-1">{stat?.value}</div>
                <div className="text-[9px] sm:text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">{stat?.label}</div>
              </div>
            )}
          </div>

          {/* Hero Images Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8 reveal">
            {[
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc153126-1764661263087.png", alt: 'Social media analytics dashboard showing engagement metrics, growth charts, and performance indicators' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_16cfb10c1-1764642188769.png", alt: 'Team of designers collaborating on creative digital marketing projects' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_11e509ff8-1766566874630.png", alt: 'Business meeting discussing digital marketing strategies and campaigns' }]?.map((img, idx) =>
            <div
              key={idx}
              className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden group hover-lift"
              style={{ animationDelay: `${idx * 150}ms` }}>
              
                <AppImage
                src={img?.src}
                alt={img?.alt}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-110" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-all" />
              </div>
            )}
          </div>

          {/* Additional Showcase Images */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 reveal">
            {[
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_168911ce6-1766404084955.png", alt: 'Data visualization and analytics for social media performance tracking' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1329dd213-1767866178992.png", alt: 'Professional team working on creative design projects' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_11b3ce77a-1764641104635.png", alt: 'Modern office workspace with digital marketing team collaboration' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_17e5ce72b-1764676806087.png", alt: 'Creative brainstorming session for brand strategy development' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1755696ab-1764874727504.png", alt: 'Business professionals discussing digital transformation strategies' }]?.map((img, idx) =>
            <div
              key={idx}
              className="relative aspect-square rounded-lg sm:rounded-xl overflow-hidden group hover-lift"
              style={{ animationDelay: `${idx * 100}ms` }}>
              
                <AppImage
                src={img?.src}
                alt={img?.alt}
                className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110" />
              
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);


}